import { DataGrid } from "devextreme-react";
import { Column, DataGridRef } from "devextreme-react/data-grid";
import { cellRenderer, customHeader } from "../SAR/SARGrid";
import type { LTIPPlan } from "@/types/types";
import { GridSkeleton } from "@/components/Skeletons";
import { formatNumber } from "devextreme/localization";
import { useRef } from "react";
import { adjustSortArrowPosition, checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import { useLTIP } from "@/hooks/useLTIP";
import { LTIPType } from "@/types/enums";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useParams } from "react-router";
import NoDataMessage from "@/components/NoDataMessage";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import Heading from "@/components/Heading";
import { NoContactBirthDateMessage } from "../HouseholdContact";

export default function DeferredCompensationPlan() {
  const dxGridRef = useRef<DataGridRef>(null);
  const { id } = useParams();
  const { household, upLoading } = useHeaderChange(id!, "Deferred Compensation Plan");
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.DCP);

  const {
    data: dcpData,
    isLoading: dcpIsLoading,
    isError: dcpIsError,
    isRefetching: dcpIsRefetching,
  } = useLTIP(id!, LTIPType.DCP, selectedClient, household);

  useDefaultSelectedClient(household, dcpData, selectedClient, setSelectedClient, LTIPType.DCP);

  const fa = dcpData?.data[0];
  const ds = fa?.planYears;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {upLoading ? (
        <div className="max-w-[16rem]">
          <GridSkeleton columns={2} rows={1} />
        </div>
      ) : household?.secondaryContact && dcpData?.bothClientsHaveData ? (
        <HouseholdClientTabs
          household={household}
          selectedClientExternalCode={selectedClient}
          setSelectedClientExternalCode={setSelectedClient}
        />
      ) : null}
      {dcpIsLoading || dcpIsRefetching ? <DCPSkeleton /> : null}
      {!ds?.length && !dcpIsLoading && !dcpIsRefetching ? <NoDataMessage /> : null}
      {dcpIsError ? <div>Failed fetching Deferred Compensation Plan</div> : null}
      {ds?.length && !dcpIsLoading && !dcpIsRefetching ? (
        <>
          {!dcpData?.bothClientsHaveData && !household?.isSingle ? (
            <Heading
              text={`Deferred Compensation Plan for ${getOwnerName(household, selectedClient)}`}
            />
          ) : null}
          <DataGrid
            dataSource={ds ?? []}
            showBorders={true}
            showRowLines={true}
            showColumnLines={true}
            keyExpr={"externalCode"}
            className="ao-grid"
            ref={dxGridRef}
            wordWrapEnabled={true}
            onCellPrepared={(e) => {
              if (e.rowType === "header" || e.column.dataField === "planYear") {
                e.cellElement.style.backgroundColor = "var(--primary-color)";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "500";
              }
              adjustSortArrowPosition(dxGridRef);
            }}
          >
            <Column
              dataField="planYear"
              dataType="date"
              allowSorting={false}
              sortOrder={"asc"}
              allowEditing={false}
              width={160}
              alignment={"center"}
              headerCellRender={() => customHeader(`plan-year`, "plan year")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.year ? cellRenderer(cellData, "plan-year", item.year) : "";
              }}
            />
            <Column
              dataField="baseSalary"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`base-salary`, "base salary")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.baseSalary
                  ? cellRenderer(
                      cellData,
                      "base-salary",
                      formatNumber(item?.baseSalary / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="incentiveCompensation"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`inventive-compensation`, "incentive", "compensation")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.incentiveCompensation
                  ? cellRenderer(
                      cellData,
                      "inventive-compensation",
                      formatNumber(item?.incentiveCompensation / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="distributionElection"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`distribution-election`, "distribution election")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.distributionElection
                  ? cellRenderer(cellData, "distribution-election", item?.distributionElection)
                  : "";
              }}
            />
            <Column
              dataField="firstPaymentDate"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`first-payment-date`, "first payment date")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.firstPaymentDate
                  ? cellRenderer(cellData, "first-payment-date", item?.firstPaymentDate)
                  : "";
              }}
            />
            <Column
              dataField="oneTimeChangeUsed"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`one-time-change-used`, "One-time", "change used")
              }
              cellRender={(cellData) =>
                fa?.oneTimeChangeUsed
                  ? cellRenderer(
                      cellData,
                      "one-time-change-used",
                      fa?.oneTimeChangeUsed ? "yes" : "no"
                    )
                  : ""
              }
            />
          </DataGrid>
        </>
      ) : null}
    </div>
  );
}

export const DCPSkeleton = () => {
  return <GridSkeleton columns={7} rows={4} />;
};
